/*
 * Copyright (c) 2022, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useQuery } from '@tanstack/react-query'

// Removes focus for non-keyboard interactions for the whole application
import 'focus-visible/dist/focus-visible'

import { getConfig } from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import { getAppOrigin } from '@salesforce/pwa-kit-react-sdk/utils/url'
import { withLegacyGetProps } from '@salesforce/pwa-kit-react-sdk/ssr/universal/components/with-legacy-get-props'
import { withReactQuery } from '@salesforce/pwa-kit-react-sdk/ssr/universal/components/with-react-query'
import { useServerContext } from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import componentMapping from '@/components/cms'
import { resolveLocaleFromUrl } from '@/utils/utils'
import { resolveSiteFromUrl } from '@/utils/site-utils'
import { createUrlTemplate } from '@/utils/url'
import {
  IntegrationsProvider,
  IntegrationsConfig,
  CommerceAPI,
  MultiSiteProvider,
  SFRAApiClient,
  SFRAAPIProvider,
  isServer,
  BasketProvider,
  CommerceAPIProvider,
  CustomerProductListsProvider,
  CustomerProvider,
  ConfigProvider,
  CookieProvider,
  BrazeProvider
} from 'msp-integrations'
import { CaptchaProvider } from 'msp-components'
import { getJasminConfig } from '../../../config/jasmin'

// Recommended settings for PWA-Kit usages.
// NOTE: they will be applied on both server and client side.
const options = {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        retry: false,
        staleTime: 120000,
        ...(isServer ? { retryOnMount: false } : {}),
        // Option for debugging changes in cache with React Query Dev Tools
        refetchOnWindowFocus: false
      },
      mutations: {
        retry: false
      }
    }
  }
}

/**
 * Use the AppConfig component to inject extra arguments into the getProps
 * methods for all Route Components in the app – typically you'd want to do this
 * to inject a connector instance that can be used in all Pages.
 *
 * You can also use the AppConfig to configure a state-management library such
 * as Redux, or Mobx, if you like.
 */
const AppConfig = ({ children, locals = {} }: any) => {
  const { req } = useServerContext()
  const [basket, setBasket] = useState(null)
  const [customer, setCustomer] = useState(null)

  const config = getConfig()
  const JasminConfig = getJasminConfig(config)

  // Set native app state
  const { data } = useQuery({
    queryKey: ['native-app-state'],
    queryFn: async () => {
      return {
        appMode: req?.headers['avoury-token'] ? '1' : '0',
        initialAuthToken: req?.headers['avoury-token'] || null,
        initialRefreshToken: req?.headers['avoury-refresh-token'] || null,
        hideHeader:
          req?.headers['avoury-hide-header'] &&
          req?.headers['avoury-hide-header'] === '1',
        hideFooter:
          req?.headers['avoury-hide-footer'] &&
          req?.headers['avoury-hide-footer'] === '1',
        cookieHandler: req?.headers['avoury-cookie-handler'] || null
      }
    },
    staleTime: 0,
    cacheTime: Infinity,
    retryOnMount: false,
    refetchOnMount: false
  })

  // Set initial token, if we are in native app
  locals.api.setInitialToken(data?.initialAuthToken, data?.initialRefreshToken)

  return (
    <>
      <MultiSiteProvider
        site={locals.site}
        locale={locals.locale}
        buildUrl={locals.buildUrl}
      >
        <IntegrationsProvider config={locals.integrationsConfig}>
          <ConfigProvider config={JasminConfig}>
            <CookieProvider gtmId={config.externalIntegrations.gtmId}>
              <CommerceAPIProvider value={locals.api}>
                <SFRAAPIProvider value={locals.sfraApi}>
                  <BrazeProvider
                    sdkKey={config.externalIntegrations.brazeSDKKey}
                    brazeUrl={config.externalIntegrations.brazeURL}
                  >
                    <CustomerProvider value={{ customer, setCustomer }}>
                      <BasketProvider value={{ basket, setBasket }}>
                        <CustomerProductListsProvider>
                          <CaptchaProvider>{children}</CaptchaProvider>
                        </CustomerProductListsProvider>
                      </BasketProvider>
                    </CustomerProvider>
                  </BrazeProvider>
                </SFRAAPIProvider>
              </CommerceAPIProvider>
            </CookieProvider>
          </ConfigProvider>
        </IntegrationsProvider>
      </MultiSiteProvider>
      {locals.reactQueryDevTools ? (
        <ReactQueryDevtools initialIsOpen={false} />
      ) : null}
    </>
  )
}

AppConfig.restore = (locals: any = {}) => {
  const path =
    typeof window === 'undefined'
      ? locals.originalUrl
      : `${window.location.pathname}${window.location.search}`
  const site = resolveSiteFromUrl(path)
  const locale = resolveLocaleFromUrl(path)
  const currency = locale.preferredCurrency

  const { app: appConfig } = getConfig()
  const apiConfig = {
    ...appConfig.commerceAPI,
    einsteinConfig: appConfig.einsteinAPI
  }
  apiConfig.parameters.siteId = site.id
  appConfig.sfraApi.parameters.siteId = site.id

  const integrationsConfig: IntegrationsConfig = {
    sfraApiConfig: {
      headers: appConfig.sfraApi.headers,
      hostname: `${getAppOrigin()}${appConfig.sfraApi.proxyPath}`,
      locale: locale.id,
      siteId: site.id,
      componentMapping
    },
    sfscApiConfig: {
      hostname: `${getAppOrigin()}${appConfig.sfscApi.proxyPath}`
    }
  }

  locals.api = new CommerceAPI({ ...apiConfig, locale: locale.id, currency })
  locals.buildUrl = createUrlTemplate(
    appConfig,
    site.alias || site.id,
    locale.id
  )
  locals.site = site
  locals.locale = locale
  locals.sfraApi = new SFRAApiClient({
    ...appConfig.sfraApi,
    locale: locale.id,
    currency
  })
  locals.integrationsConfig = integrationsConfig
  locals.reactQueryDevTools = appConfig.frontend?.reactQueryDevTools || false
}

AppConfig.freeze = () => undefined

AppConfig.extraGetPropsArgs = (locals: any = {}) => {
  return {
    api: locals.api,
    buildUrl: locals.buildUrl,
    site: locals.site,
    locale: locals.locale,
    reactQueryDevTools: locals.reactQueryDevTools
  }
}

AppConfig.propTypes = {
  children: PropTypes.node,
  locals: PropTypes.object
}

export default withReactQuery(withLegacyGetProps(AppConfig), options)
